import React, { useCallback, useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import type { HandoverLeg } from '@/components';
import {
  bastHrefFor,
  Button,
  ConfirmDialog,
  Field,
  formatHandoverOdometer,
  ImagePreview,
  SectionBand,
  Status,
} from '@/components';
import { RENT_TYPE_LABEL } from '@/types/package';
import {
  ChargeStatus,
  RentalDetail,
  HandoverStatus,
  RefundPayoutStatus,
} from '@/types/rental';
import {
  cancelRental,
  downloadRentalGuarantorLetter,
  getRentalById,
  reassignVehicle,
  retryRefundPayout,
} from '@/services/api/rentals';
import {
  createHandover,
  CreateHandoverBody,
  CorrectHandoverChecklistResponse,
} from '@/services/api/opsHandovers';
import { useDocumentReissueWatch } from './useDocumentReissueWatch';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showWarningToast,
} from '@/components/toast/globalToast';
import { downloadBlob, extractApiErrorMessage, formatRupiah } from '@/utils';
import { useListReturnPath } from '@/hooks/useUrlListState';
import {
  canCancelRental,
  canCorrectHandoverChecklist,
  canRetryRefund,
  computeDurationDays,
  computeRemainingDeposit,
  arrearsCutoffToneClass,
  buildDepositRows,
  describeReissuedDocuments,
  escalationToneClass,
  formatArrears,
  formatArrearsCutoff,
  formatDate,
  formatFreeDayDates,
  formatVehicleLabel,
  hasDepositStory,
  needsRefundAttention,
  REFUNDABLE_STAGE_LABEL,
  refundableStageToneClass,
  refundStatusLabel,
  RENTAL_DOCUMENT_STAGE_LABEL,
  resolveRentalStatusKey,
  showsEscalationPill,
  canRequestRepossession,
  repossessionLinkLabel,
} from './rentalDisplay';
import RentalTimeline from './components/RentalTimeline';
import PaymentHistoryTab from './components/PaymentHistoryTab';
import ChargeLedgerTab, {
  ChargeStatusFilter,
} from './components/ChargeLedgerTab';
import CorrectHandoverChecklistModal from './components/CorrectHandoverChecklistModal';
import CreateHandoverModal from './components/CreateHandoverModal';
import HandoverInfoModal from './components/HandoverInfoModal';
import ReassignVehicleModal from './components/ReassignVehicleModal';
import RequestRepossessionModal from './components/RequestRepossessionModal';

/**
 * "12/03/2025 • 12.480 km" — whichever half of it the leg actually recorded.
 * Odometer wording comes from the handover components so the card agrees with
 * the modal it opens.
 */
const formatLegMeta = (date: string | null, odometer: number | null): string =>
  [
    formatDate(date, ''),
    odometer === null ? '' : formatHandoverOdometer(odometer),
  ]
    .filter(Boolean)
    .join(' • ');

/**
 * One leg of the handover row, styled like the handover list's Vehicle cell so
 * the two screens read as the same object. Opens that leg's evidence; the leg's
 * BAST is listed once, under Dokumen.
 */
const HandoverLegCard: React.FC<{
  badge: string;
  plate: string;
  vehicleLabel: string;
  meta: string;
  onClick: () => void;
}> = ({ badge, plate, vehicleLabel, meta, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary"
  >
    <span className="block">
      <span className="block text-[14px] font-medium">{plate}</span>
      <span className="block text-[12px] text-gray-500">{vehicleLabel}</span>
      <span className="block text-[12px] text-gray-500 mt-1.5">{meta}</span>
    </span>
    <span className="flex items-center gap-2 shrink-0">
      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[12px] text-gray-600">
        {badge}
      </span>
      <i className="ri-arrow-right-s-line text-[18px] text-gray-400"></i>
    </span>
  </button>
);

/**
 * One document in the rent's paper trail, styled like the handover leg cards so
 * the two lists read as the same screen.
 *
 * The action is what distinguishes the three: `href` opens an issued PDF that
 * already exists in the bucket, `onDownload` renders one on request (the Surat
 * Penjamin has no stored copy to link to), and neither means it has not been
 * issued yet — which renders as a STATE, never as a dead link.
 */
const DocumentRow: React.FC<{
  label: string;
  /** Which stage or leg issued this copy, when a document has several. */
  badge?: string;
  meta?: string;
  href?: string | null;
  onDownload?: () => void;
  isDownloading?: boolean;
  /** A checklist correction is re-rendering this copy; its link is the old one. */
  isReissuing?: boolean;
}> = ({ label, badge, meta, href, onDownload, isDownloading, isReissuing }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-medium text-gray-900">{label}</span>
        {badge && (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[12px] text-gray-600">
            {badge}
          </span>
        )}
      </div>
      {meta && (
        <p className="mt-1 truncate text-[12px] text-gray-500">{meta}</p>
      )}
    </div>
    {isReissuing ? (
      <span className="inline-flex shrink-0 items-center gap-1 text-[12px] text-gray-500">
        <i className="ri-loader-4-line animate-spin text-[14px]"></i>
        Membuat ulang…
      </span>
    ) : href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex shrink-0 items-center gap-1 rounded border border-purple-200 px-2 py-1 text-[12px] font-medium text-purple-600 transition-colors hover:bg-purple-50"
      >
        <i className="ri-file-pdf-2-line text-[14px]"></i>
        Buka
      </a>
    ) : onDownload ? (
      <button
        type="button"
        onClick={onDownload}
        disabled={isDownloading}
        className="inline-flex shrink-0 items-center gap-1 rounded border border-purple-200 px-2 py-1 text-[12px] font-medium text-purple-600 transition-colors hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i
          className={
            isDownloading
              ? 'ri-loader-4-line animate-spin text-[14px]'
              : 'ri-download-2-line text-[14px]'
          }
        ></i>
        {isDownloading ? 'Menyiapkan' : 'Unduh'}
      </button>
    ) : (
      <span className="shrink-0 text-[12px] text-gray-400">Belum tersedia</span>
    )}
  </div>
);

const RentalDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const backTo = useListReturnPath('/rent-activity');
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [detail, setDetail] = useState<RentalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActing, setIsActing] = useState(false);
  const [isRetryingRefund, setIsRetryingRefund] = useState(false);
  const [isDownloadingGuarantor, setIsDownloadingGuarantor] = useState(false);
  // Pembayaran yang sedang ditelusuri di tab Rincian Tagihan. Disimpan di
  // sini, bukan di tab-nya, karena yang memilihnya tab sebelahnya.
  const [chargeCollectionFilter, setChargeCollectionFilter] = useState<{
    ref: string;
    code: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'detail' | 'payments' | 'charges'>(
    'detail'
  );
  // Lives here rather than inside the ledger tab so the Tunggakan figure can
  // set it on the way in — the tab unmounts when it is not the active one, so
  // state owned by the tab could not be aimed from outside.
  const [chargeStatusFilter, setChargeStatusFilter] =
    useState<ChargeStatusFilter>('ALL');
  const [isHandoverOpen, setIsHandoverOpen] = useState(false);
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRepoRequestOpen, setIsRepoRequestOpen] = useState(false);
  const [isChecklistCorrectionOpen, setIsChecklistCorrectionOpen] =
    useState(false);
  const [openLeg, setOpenLeg] = useState<HandoverLeg | null>(null);

  // Re-reads without the page's skeleton, for refreshes ops did not ask for.
  const refreshSilently = useCallback(async () => {
    const fresh = await getRentalById(id ?? '');
    setDetail(fresh);
    return fresh;
  }, [id]);

  const loadRental = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await refreshSilently();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load rental')
      );
      navigate(backTo);
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, backTo, refreshSilently]);

  useEffect(() => {
    loadRental();
  }, [loadRental]);

  const reissue = useDocumentReissueWatch({
    rentalID: id,
    documents: detail?.documents,
    // Only the documents block moves while a reissue renders.
    onDocuments: (documents) =>
      setDetail((prev) =>
        prev &&
        JSON.stringify(prev.documents) !== JSON.stringify(documents)
          ? { ...prev, documents }
          : prev
      ),
    onLanded: (documents) =>
      showSuccessToast(
        'Dokumen diperbarui',
        `${describeReissuedDocuments(documents)} sudah dibuat ulang.`
      ),
    onGaveUp: (documents) =>
      showWarningToast(
        'Belum terbit ulang',
        `${describeReissuedDocuments(
          documents
        )} belum juga dibuat ulang. Hubungi tim teknis untuk membuatnya ulang.`
      ),
  });

  // `?action=request-repo` — the list's three-dot lands here with the form
  // already open. Consumed once (param cleared) so a reload doesn't re-open
  // it; only honoured when the gates below would render the button anyway.
  useEffect(() => {
    if (!detail || searchParams.get('action') !== 'request-repo') return;
    if (canRequestRepossession(detail)) {
      setIsRepoRequestOpen(true);
    }
    // Carry the router state over: it holds the list URL Back returns to.
    setSearchParams({}, { replace: true, state: location.state });
  }, [detail, searchParams, setSearchParams, location.state]);

  const snapshot = detail?.termsSnapshot;

  // Server-computed now: the detail used to ship charges[]/payments[] and the
  // screen counted them, which meant transferring a row per rental day to
  // render two numbers.
  const plan = detail?.plan;
  const depositInstallment = detail?.depositInstallment;
  const tenor = detail?.tenor;
  const handover = detail?.handover ?? null;
  const remainingDeposit = detail ? computeRemainingDeposit(detail) : null;
  // Absent on a backend older than 2026-08-10, so the whole block is optional
  // rather than rendering a row of dashes.
  const refundable = detail?.refundable ?? null;
  const depositRows = refundable ? buildDepositRows(refundable) : [];
  const arrearsLabel = formatArrears(detail?.arrears);
  const arrearsCutoff = formatArrearsCutoff(detail?.arrears?.startAt);

  // The rent's paper trail. `documents` ships with the 2026-09-02 backend, so
  // each link falls back to the handover row an older deployment would be
  // serving — the section renders the same either way.
  const rentalAgreements = detail?.documents?.rentalAgreements ?? [];
  const bastHandoverUrl =
    detail?.documents?.bast.handoverLink || handover?.bastLink || null;
  const bastReturnUrl =
    detail?.documents?.bast.returnLink || handover?.returnBastLink || null;

  // Both legs live on one handover row, so the return card only exists once the
  // unit is actually back.
  const hasReturnLeg = Boolean(handover?.returnDate);

  /**
   * The Surat Penjamin has no stored copy to link to — it is rendered per
   * request — so this is a fetch-then-save rather than an anchor.
   *
   * A 403 here is the answer, not a failure of the screen: rent eligibility is
   * the sheet's only condition, and the reason it carries ("KYC belum tuntas")
   * is what tells ops what to fix. Surfaced verbatim.
   */
  const handleDownloadGuarantorLetter = async () => {
    if (!id) return;
    setIsDownloadingGuarantor(true);
    try {
      const blob = await downloadRentalGuarantorLetter(id);
      downloadBlob(`surat-penjamin-${detail?.code || id}.pdf`, blob);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal mengunduh Surat Penjamin')
      );
    } finally {
      setIsDownloadingGuarantor(false);
    }
  };

  const runAction = async (label: string, action: () => Promise<unknown>) => {
    setIsActing(true);
    try {
      await action();
      showSuccessToast('Success', label);
      await loadRental();
    } catch (error) {
      showErrorToast('Error', extractApiErrorMessage(error, 'Action failed'));
    } finally {
      setIsActing(false);
    }
  };

  // The reissue renders in the background, so the rows reloaded here still
  // carry the old copies for a moment — the toast says so.
  const handleChecklistCorrected = async (
    result: CorrectHandoverChecklistResponse
  ) => {
    setIsChecklistCorrectionOpen(false);
    showInfoToast(
      'Checklist dikoreksi',
      `${describeReissuedDocuments(result.reissuing)} sedang dibuat ulang.`
    );
    // Picks up the new checklist and the server's pending reissue at once.
    await refreshSilently().catch(() =>
      showWarningToast(
        'Gagal memuat ulang',
        'Muat ulang halaman ini untuk melihat dokumen yang sedang dibuat ulang.'
      )
    );
  };

  const handleHandover = async (
    body: Omit<CreateHandoverBody, 'handoverID'>
  ) => {
    // Keyed on the handover row, which is what the endpoint moves to ACTIVE.
    // The button is gated on `handoverStatus`, so a missing row here is not a
    // reachable state — it is a guard, not a branch users see.
    if (!handover?.ID) {
      showErrorToast('Error', 'Rental ini belum punya handover untuk diproses');
      return;
    }
    setIsHandoverOpen(false);
    await runAction('Handover selesai, sewa aktif', () =>
      createHandover({ ...body, handoverID: handover.ID })
    );
  };

  const handleRetryRefund = async (payoutID: string) => {
    setIsRetryingRefund(true);
    try {
      await retryRefundPayout(payoutID);
      showSuccessToast('Success', 'Refund diinstruksikan ulang');
      await loadRental();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal instruksikan ulang refund')
      );
    } finally {
      setIsRetryingRefund(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-gray-600">Loading rental...</span>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            aria-label="Kembali"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <i className="ri-arrow-left-s-line text-[18px]"></i>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[20px] font-medium text-gray-900">
                Detail Rent
              </h1>
              {/* Both dimensions, side by side. Neither is sufficient alone: the
                  contract reads ACTIVE whether the bike is at the hub or on the
                  road, and possession says nothing about whether the rental was
                  cancelled or ran its term. */}
              <Status
                value={resolveRentalStatusKey(detail)}
                type="rentalStatus"
                size="small"
              />
              {detail.handoverStatus && (
                <Status
                  value={detail.handoverStatus}
                  type="handoverStatus"
                  size="small"
                />
              )}
            </div>
            <p className="text-[14px] text-gray-500 mt-0.5">
              {[detail.code, detail.driver?.code, detail.vehicle?.platNomor]
                .filter(Boolean)
                .join(' • ') || '-'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* A booking the driver no longer wants (another package, say): the
              new booking needs this one gone first. */}
          {canCancelRental(detail) && (
            <Button
              variant="white"
              size="medium"
              onClick={() => setIsCancelOpen(true)}
              disabled={isActing}
            >
              <i className="ri-close-circle-line text-[14px] mr-1"></i>
              Batalkan Booking
            </Button>
          )}
          {/* Possession, not the contract: the rental is ACTIVE both before and
              after pickup, so gating on its status would offer the Handover
              button for a unit already out with the driver. */}
          {detail.handoverStatus === HandoverStatus.WAITING_HANDOVER && (
            <>
              {/* Same pre-pickup window the backend enforces (TRD B6): once
                  the unit is out, a swap is a return + a new rental. */}
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setIsReassignOpen(true)}
                disabled={isActing}
              >
                <i className="ri-swap-line text-[14px] mr-1"></i>
                Ganti Unit
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={() => setIsHandoverOpen(true)}
                disabled={isActing}
              >
                <i className="ri-exchange-line text-[14px] mr-1"></i>
                Handover
              </Button>
            </>
          )}
          {/* The escalation ladder said it's repo time (or the unit is already
              shut down) and the unit is out with the driver — the only state
              POST /v1/repossessions accepts a case for. */}
          {canRequestRepossession(detail) && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => setIsRepoRequestOpen(true)}
              disabled={isActing}
            >
              <i className="ri-car-line text-[14px] mr-1"></i>
              Request Repossession
            </Button>
          )}
          {detail.repossession && (
            <Button
              variant="white"
              size="medium"
              onClick={() =>
                navigate(`/repossessions/${detail.repossession!.ID}`)
              }
            >
              <i className="ri-car-line text-[14px] mr-1"></i>
              {repossessionLinkLabel(detail.repossession)}
            </Button>
          )}
        </div>
      </div>

      {/* Above the tabs, not inside one: money owed to a driver that no payout
          is chasing should not be reachable only by opening Riwayat Pembayaran.
          Silent on PENDING/COMPLETED — those need no ops action. */}
      {needsRefundAttention(detail.refund) && detail.refund && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#F5C2C7] bg-[#FFF5F5] px-4 py-3">
          <div className="flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 text-[16px] text-[#B3131F]"></i>
            <div>
              <p className="text-[14px] font-medium text-[#B3131F]">
                {refundStatusLabel(detail.refund.payoutStatus)}
                {detail.refund.amount
                  ? ` — ${formatRupiah(detail.refund.amount)}`
                  : ''}
              </p>
              <p className="mt-0.5 text-[13px] text-gray-600">
                {detail.refund.payoutStatus === RefundPayoutStatus.FAILED
                  ? 'Payout sudah dikirim tapi ditolak bank. Instruksikan ulang membuat percobaan baru — bereskan dulu penyebabnya (mis. rekening mitra).'
                  : 'Dana belum dikirim ke mitra dan tidak ada payout yang berjalan. Instruksikan ulang setelah rekening KYC mitra lengkap.'}
              </p>
            </div>
          </div>
          {canRetryRefund(detail.refund) && (
            <Button
              variant="primary"
              size="small"
              onClick={() => handleRetryRefund(detail.refund!.payoutID)}
              disabled={isRetryingRefund}
            >
              {isRetryingRefund ? 'Memproses...' : 'Instruksikan Ulang Refund'}
            </Button>
          )}
        </div>
      )}

      <div className="border-b border-gray-200 mb-5">
        <nav className="flex gap-6" aria-label="Detail Rent sections">
          {[
            { key: 'detail' as const, label: 'Detail' },
            { key: 'payments' as const, label: 'Riwayat Pembayaran' },
            { key: 'charges' as const, label: 'Rincian Tagihan' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              aria-current={activeTab === tab.key ? 'page' : undefined}
              className={`-mb-px border-b-2 px-1 pb-3 text-[15px] transition-colors ${
                activeTab === tab.key
                  ? 'border-primary font-medium text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'payments' && id && (
        <PaymentHistoryTab
          rentalId={id}
          onPaymentSettled={loadRental}
          onViewSettledCharges={(collection) => {
            setChargeCollectionFilter({
              ref: collection.id,
              code: collection.code || collection.id,
            });
            // Status dilepas: menyempitkan ke satu pembayaran lalu menahan
            // filter PENDING dari kunjungan sebelumnya akan selalu kosong,
            // karena hari yang dibayar tidak mungkin masih PENDING.
            setChargeStatusFilter('ALL');
            setActiveTab('charges');
          }}
        />
      )}

      {activeTab === 'charges' && id && (
        <ChargeLedgerTab
          rentalId={id}
          waivers={detail.waivers}
          statusFilter={chargeStatusFilter}
          onStatusFilterChange={setChargeStatusFilter}
          collectionFilter={chargeCollectionFilter}
          onClearCollectionFilter={() => setChargeCollectionFilter(null)}
        />
      )}

      {activeTab === 'detail' && (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="w-full lg:flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4">
              <h2 className="text-[18px] font-medium text-gray-900">
                Informasi Kendaraan
              </h2>
            </div>

            <SectionBand>Detail Mitra</SectionBand>
            {/* The mitra, and only the mitra. This used to carry a "Document"
                column whose single BAST link was the very same
                `handover.bastLink` the Handover Information card below already
                offers — one document in two places, and the rental's other two
                documents in none. Both are now in the Dokumen section. */}
            <div className="px-5 py-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-primary font-medium">
                {detail.driver?.name?.charAt(0)?.toUpperCase() ?? '?'}
              </div>
              <div>
                <p className="text-[16px] font-medium text-gray-900">
                  {detail.driver?.name || '-'}
                </p>
                <p className="text-[13px] text-gray-500">
                  {detail.driver?.code || '-'}
                </p>
              </div>
            </div>

            {/* The three documents a rental has, in the order the contract
                produces them. Each renders its own absence rather than
                disappearing: "belum ada BAST" is information an operator
                needs, and a missing row would read as a missing feature. */}
            <SectionBand>Dokumen</SectionBand>
            <div className="px-5 py-4 space-y-2">
              {/* fleet-app forces an answer per part, so a wrong one is a
                  mis-tap ops notices later — usually at the return. */}
              {canCorrectHandoverChecklist(detail) && (
                <div className="flex flex-col gap-2 rounded-lg bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[13px] text-gray-600">
                    Checklist serah terima salah? Koreksi, lalu BAST dan
                    Perjanjian Sewa Kontrak dibuat ulang.
                  </p>
                  <Button
                    variant="secondary"
                    size="small"
                    className="shrink-0 whitespace-nowrap"
                    onClick={() => setIsChecklistCorrectionOpen(true)}
                  >
                    <i className="ri-edit-2-line mr-1"></i>
                    Koreksi Checklist
                  </Button>
                </div>
              )}
              {/* One issued copy per stage, most complete first. Every copy is
                  listed, not just the newest: the BOOKING copy is the one that
                  was actually signed, and it stays valid after the return. */}
              {rentalAgreements.length > 0 ? (
                rentalAgreements.map((document) => (
                  <DocumentRow
                    key={document.type}
                    label="Perjanjian Sewa Kontrak"
                    badge={
                      RENTAL_DOCUMENT_STAGE_LABEL[document.type] ||
                      document.type
                    }
                    meta={[
                      document.number,
                      formatDate(document.generatedAt, ''),
                    ]
                      .filter(Boolean)
                      .join(' • ')}
                    href={bastHrefFor(document.link)}
                    isReissuing={reissue.isReissuing(document.type)}
                  />
                ))
              ) : (
                <DocumentRow
                  label="Perjanjian Sewa Kontrak"
                  meta="Digenerate otomatis setelah DP lunas"
                  isReissuing={reissue.isReissuing('HANDOVER')}
                />
              )}

              {/* Two legs of one handover row, so the back leg's document does
                  not exist until the unit is actually returned. Nothing
                  generates a return BAST yet, so `returnBastLink` is the file
                  ops uploaded through the return form — this list is where
                  they come to look for it. */}
              {bastHandoverUrl || bastReturnUrl ? (
                <>
                  {bastHandoverUrl && (
                    <DocumentRow
                      label="BAST"
                      badge="Serah Terima"
                      meta={formatLegMeta(
                        handover?.handoverDate ?? null,
                        handover?.odometer ?? null
                      )}
                      href={bastHrefFor(bastHandoverUrl)}
                      isReissuing={reissue.isReissuing('BAST')}
                    />
                  )}
                  {bastReturnUrl && (
                    <DocumentRow
                      label="BAST"
                      badge="Pengembalian"
                      meta={formatLegMeta(
                        handover?.returnDate ?? null,
                        handover?.returnOdometer ?? null
                      )}
                      href={bastHrefFor(bastReturnUrl)}
                    />
                  )}
                </>
              ) : (
                <DocumentRow
                  label="BAST"
                  meta="Digenerate otomatis setelah serah terima"
                  isReissuing={reissue.isReissuing('BAST')}
                />
              )}

              {/* No availability state to render: the sheet is a blank FORM
                  rendered on request and never stored, so there is nothing to
                  wait for. Its only condition is rent eligibility, which the
                  403 explains — see handleDownloadGuarantorLetter. */}
              <DocumentRow
                label="Surat Penjamin"
                meta="Formulir tanda tangan — dibuat saat diunduh"
                onDownload={handleDownloadGuarantorLetter}
                isDownloading={isDownloadingGuarantor}
              />
            </div>

            <SectionBand>Detail Vehicle</SectionBand>
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Plate Number">
                {detail.vehicle?.platNomor || '-'}
              </Field>
              <Field label="Status">
                {detail.vehicle?.status ? (
                  <Status
                    value={detail.vehicle.status.toLowerCase()}
                    type="feedback"
                    size="small"
                  />
                ) : detail.vehicle ? (
                  <span className="text-gray-400">-</span>
                ) : (
                  <span className="text-gray-400">Belum ditugaskan</span>
                )}
              </Field>
              <Field label="OEM Name">{detail.vehicle?.oemName || '-'}</Field>
              <Field label="Model Name">
                {detail.vehicle?.modelName || '-'}
              </Field>
              {/* VIN, engine number, colour and photo are not in the documented
                rental contract — they render once the shape widens. */}
              <Field label="Nomor Rangka/VIN">
                {detail.vehicle?.vin || '-'}
              </Field>
              <Field label="Nomor Mesin">
                {detail.vehicle?.engineNumber || '-'}
              </Field>
              <Field label="IMEI">{detail.vehicle?.imei || '-'}</Field>
              <Field label="Warna">{detail.vehicle?.color || '-'}</Field>
              <Field label="Foto Kendaraan">
                {detail.vehicle?.imageURL ? (
                  <ImagePreview
                    src={detail.vehicle.imageURL}
                    alt="Foto kendaraan"
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </Field>
            </div>

            <SectionBand>Detail Sewa</SectionBand>
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Paket">{detail.package?.name || '-'}</Field>
              <Field label="Contract Type">
                {detail.package?.contractType
                  ? `${detail.package.contractType} (${
                      RENT_TYPE_LABEL[detail.package.contractType]
                    })`
                  : '-'}
              </Field>

              <Field label="Tenor">
                {tenor?.totalDays
                  ? `${Math.round(tenor.totalDays / 30)} Bulan`
                  : '-'}
              </Field>
              <Field label="Sisa Tenor">
                {/* elapsedDays is null until handover, so an unstarted rental
                  shows the total rather than a misleading 0. */}
                {tenor?.totalDays ? (
                  <span>
                    <span className="text-primary font-medium">
                      {tenor.elapsedDays ?? 0}
                    </span>
                    {` / ${tenor.totalDays} hari`}
                  </span>
                ) : (
                  '-'
                )}
              </Field>

              <Field label="Cicilan Deposit">
                {/* coveredDays, not paidDays: the ledger only settles one
                  installment a day, so a driver who prepaid a week would read
                  as 0 here while the tenor above already credits those days. */}
                {depositInstallment?.totalDays ? (
                  <span>
                    <span className="text-primary font-medium">
                      {depositInstallment.coveredDays}
                    </span>
                    {` / ${depositInstallment.totalDays} Bayar`}
                    {plan?.installmentAmount
                      ? ` · ${formatRupiah(plan.installmentAmount, '-', { space: false })}/hari`
                      : ''}
                  </span>
                ) : (
                  '-'
                )}
              </Field>
              <Field label="Sewa/Hari">
                {formatRupiah(snapshot?.dailyPrice, '-', { space: false })}
              </Field>

              <Field label="Deposit">
                {formatRupiah(snapshot?.deposit, '-', { space: false })}
              </Field>
              <Field label="Sisa Deposit">
                {/* Counts the instalments settled so far, not just the DP —
                  otherwise the figure never moves for the whole rental. */}
                {formatRupiah(remainingDeposit, '-', { space: false })}
              </Field>

              <Field label="Minimal Masa Sewa - Hari Gratis">
                {snapshot?.freeDayMinTenureDays !== undefined
                  ? `${snapshot.freeDayMinTenureDays} Hari`
                  : '-'}
              </Field>
              <Field label="Tanggal Gratis">
                {formatFreeDayDates(snapshot?.freeDayDates) ?? '-'}
              </Field>

              <Field label="Handover Date">
                {formatDate(detail.handoverDate)}
              </Field>
              <Field label="Complete Date">{formatDate(detail.endDate)}</Field>

              <Field label="Durasi Sewa">
                {computeDurationDays(detail.handoverDate, detail.endDate)} Hari
              </Field>
              <Field label="Eskalasi">
                {showsEscalationPill(detail.escalationStatus) ? (
                  <Status
                    value={detail.escalationStatus}
                    type="escalationStatus"
                    size="small"
                  />
                ) : (
                  <span className="text-gray-500">Normal</span>
                )}
              </Field>
              {/* Arrears never reached this screen before — the figure ops need
                in order to decide anything was list-and-CSV only. The figure
                is a link into the ledger, filtered to the unpaid days: the
                total alone says how much is owed but never which days, and
                that is the next question every time. */}
              <Field label="Tunggakan">
                {arrearsLabel ? (
                  <button
                    type="button"
                    onClick={() => {
                      setChargeStatusFilter(ChargeStatus.PENDING);
                      // Dua penyempitan sekaligus tidak mungkin berisi: hari
                      // yang sudah dibayar satu transaksi tidak PENDING.
                      setChargeCollectionFilter(null);
                      setActiveTab('charges');
                    }}
                    className={`inline-flex items-center gap-1 font-medium underline hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-primary rounded ${escalationToneClass(
                      detail.escalationStatus
                    )}`}
                    title="Lihat hari yang belum dibayar"
                  >
                    {arrearsLabel}
                    <i className="ri-arrow-right-up-line text-[14px]"></i>
                  </button>
                ) : (
                  <span className="text-gray-500">Tidak ada tunggakan</span>
                )}
                {/* Daily cutoff — the handover's time-of-day; null once the
                  charge already IS arrears, so no line renders then. */}
                {arrearsCutoff && (
                  <p
                    className={`text-[12px] mt-1 ${arrearsCutoffToneClass(
                      detail.arrears?.warningActive
                    )}`}
                  >
                    Jadi tunggakan: {arrearsCutoff}
                  </p>
                )}
              </Field>
            </div>

            {/* Its own section, not two more cells in the Detail Sewa grid.
              "Sisa Deposit" up there is deposit still to be PAID; "Deposit
              Terbayar" here is deposit already IN — near-complements moving in
              opposite directions as instalments settle, and side by side in one
              grid they are how ops reads the wrong one to a driver.

              Hidden entirely when the driver has put nothing in yet: a fresh
              unpaid booking has no deposit story, and three Rp0 rows plus a Rp0
              estimate is noise dressed as information. */}
            {refundable && hasDepositStory(refundable) && (
              <>
                <SectionBand>Informasi Deposit</SectionBand>
                <div className="px-5 py-4">
                  <div className="space-y-3">
                    {depositRows.map((row) => (
                      <div
                        key={row.label}
                        className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                      >
                        <span className="text-[13px] text-gray-500">
                          {row.label}
                          {row.hint && (
                            <span className="text-gray-400"> ({row.hint})</span>
                          )}
                        </span>
                        <span
                          className={`text-[14px] font-medium shrink-0 ${
                            row.negative ? 'text-gray-600' : 'text-gray-900'
                          }`}
                        >
                          {row.negative ? '−' : ''}
                          {formatRupiah(row.amount, '-', { space: false })}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4 border-t border-gray-100 pt-3">
                    <span className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">
                        Estimasi Dana Kembali
                      </span>
                      {/* The badge is not optional — an ESTIMATE is a ceiling
                        (damage is unknown until the unit is back, and the figure
                        moves daily as instalments settle), and an unlabelled
                        number invites ops to quote it as settled. */}
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] font-medium ${refundableStageToneClass(
                          refundable.stage
                        )}`}
                      >
                        {REFUNDABLE_STAGE_LABEL[refundable.stage] ??
                          refundable.stage}
                      </span>
                    </span>
                    <span className="text-[15px] font-semibold text-primary shrink-0">
                      {formatRupiah(refundable.estimatedNet, '-', {
                        space: false,
                      })}
                    </span>
                  </div>
                </div>
              </>
            )}

            <SectionBand>Handover Information</SectionBand>
            <div className="px-5 py-4">
              {handover ? (
                <div className="space-y-3">
                  {/* Two legs of ONE handover row, not two records — the badges
                      name the leg, and each card opens only that half. */}
                  <HandoverLegCard
                    badge="Serah Terima"
                    plate={detail.vehicle?.platNomor || '-'}
                    vehicleLabel={formatVehicleLabel(detail.vehicle) || '-'}
                    meta={formatLegMeta(
                      handover.handoverDate,
                      handover.odometer
                    )}
                    onClick={() => setOpenLeg('HANDOVER')}
                  />
                  {hasReturnLeg && (
                    <HandoverLegCard
                      badge="Pengembalian"
                      plate={detail.vehicle?.platNomor || '-'}
                      vehicleLabel={formatVehicleLabel(detail.vehicle) || '-'}
                      meta={formatLegMeta(
                        handover.returnDate,
                        handover.returnOdometer
                      )}
                      onClick={() => setOpenLeg('RETURN')}
                    />
                  )}
                </div>
              ) : (
                <p className="text-[14px] text-gray-400">
                  Handover belum tercatat.
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100">
              {/* Handover and return notes now live on their own leg in the
                modal — this block is the rental's own settlement note, which
                used to be shadowed by whatever the handover recorded. */}
              <p className="text-[13px] text-gray-500 mb-1">Note</p>
              <p className="text-[14px] text-gray-900">
                {detail.settlementNotes || '-'}
              </p>
            </div>

            {/* Priced at settlement (never at the physical return) — see the
              damage/outstanding TRD. Hidden entirely for a rental with no
              deductions rather than showing an empty section. */}
            {!!detail.deductions?.length && (
              <>
                <SectionBand>Deduksi Kerusakan</SectionBand>
                <div className="px-5 py-4">
                  <div className="space-y-3">
                    {detail.deductions.map((deduction, index) => (
                      <div
                        key={deduction.id || index}
                        className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                      >
                        <div>
                          <p className="text-[14px] text-gray-900">
                            {deduction.description}
                          </p>
                          {!!deduction.evidence?.length && (
                            <div className="mt-1 flex flex-wrap gap-2">
                              {deduction.evidence.map((url, photoIndex) => (
                                <a
                                  key={url}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[12px] text-primary underline"
                                >
                                  Foto {photoIndex + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-[14px] font-medium text-gray-900 shrink-0">
                          {formatRupiah(deduction.amount, '-', {
                            space: false,
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                    <span className="text-[13px] text-gray-500">
                      Total Deduksi
                    </span>
                    <span className="text-[14px] font-medium text-gray-900">
                      {formatRupiah(
                        detail.deductions.reduce((sum, d) => sum + d.amount, 0)
                      )}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* The debt this settlement left behind, when the deductions came to
              more than the deposit covered. It belongs to the MITRA, not to this
              rental — it outlives the contract — so this is a crossing point to
              the screen that owns it, not a copy of it. The outstanding screen
              links back the same way. */}
            {detail.outstanding && (
              <>
                <SectionBand>Outstanding Terkait</SectionBand>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="Status">
                    <Status
                      value={detail.outstanding.status}
                      type="driverOutstandingStatus"
                      size="small"
                    />
                  </Field>
                  <Field label="Sisa Tagihan">
                    {formatRupiah(detail.outstanding.amountOutstanding)}
                    {detail.outstanding.amountOutstanding !==
                      detail.outstanding.amountOriginal && (
                      <span className="text-[13px] text-gray-500">
                        {' '}
                        dari {formatRupiah(detail.outstanding.amountOriginal)}
                      </span>
                    )}
                  </Field>
                  <Field label="Detail">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/outstandings/${detail.outstanding!.ID}`)
                      }
                      className="inline-flex items-center gap-1 text-primary underline hover:opacity-80"
                    >
                      Lihat Outstanding
                      <i className="ri-arrow-right-up-line text-[14px]"></i>
                    </button>
                  </Field>
                </div>
              </>
            )}
          </div>

          <div className="w-full lg:w-[480px] shrink-0">
            <RentalTimeline detail={detail} />
          </div>
        </div>
      )}

      <CreateHandoverModal
        isOpen={isHandoverOpen}
        onClose={() => setIsHandoverOpen(false)}
        onSubmit={handleHandover}
        isLoading={isActing}
        detail={detail}
      />

      <CorrectHandoverChecklistModal
        isOpen={isChecklistCorrectionOpen}
        onClose={() => setIsChecklistCorrectionOpen(false)}
        onCorrected={handleChecklistCorrected}
        detail={detail}
      />

      <HandoverInfoModal
        isOpen={openLeg !== null}
        onClose={() => setOpenLeg(null)}
        detail={detail}
        leg={openLeg}
      />

      <ConfirmDialog
        isOpen={isCancelOpen}
        title="Batalkan booking?"
        description="Booking ini dibatalkan dan unitnya dilepas. DP yang sudah dibayar dikembalikan ke mitra, pembayaran DP yang belum dibayar dibatalkan. Mitra bisa langsung booking paket lain."
        confirmLabel="Batalkan Booking"
        cancelLabel="Kembali"
        confirmVariant="danger"
        isBusy={isActing}
        onCancel={() => setIsCancelOpen(false)}
        onConfirm={async () => {
          await runAction('Booking dibatalkan', () => cancelRental(detail.id));
          setIsCancelOpen(false);
        }}
      />

      <ReassignVehicleModal
        isOpen={isReassignOpen}
        onClose={() => setIsReassignOpen(false)}
        isLoading={isActing}
        detail={detail}
        onSubmit={(vehicleID, overrideReason) => {
          setIsReassignOpen(false);
          runAction('Unit berhasil diganti', () =>
            reassignVehicle(detail.id, vehicleID, overrideReason)
          );
        }}
      />

      <RequestRepossessionModal
        isOpen={isRepoRequestOpen}
        onClose={() => setIsRepoRequestOpen(false)}
        detail={detail}
        onCreated={(repoId) => {
          setIsRepoRequestOpen(false);
          navigate(repoId ? `/repossessions/${repoId}` : '/repossessions');
        }}
      />
    </div>
  );
};

export default RentalDetailPage;
